import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import installLinkAttribution from "./utils/linkAttribution";
import { logger } from "./utils/logger";
import { preloadPrerenderedChunk, selectChunkLoaders } from './routeChunks';

const container = document.getElementById("root");

// Client ID comes from the build env (REACT_APP_GOOGLE_CLIENT_ID).
//
// This used to say a missing value was harmless because "the rest of the app
// is unaffected". True, and it meant Google sign-in shipped to production on
// 2026-07-25 and never worked: the key was wired here but never added to
// .env.production, so every build baked in an empty string and the button
// died on init with nothing in the console. Sign-in is the only conversion
// path on the landing page, so silence is the expensive failure mode.
//
// Now it says so, loudly, and OtpAuthModal hides the button rather than
// showing a dead one.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

if (!GOOGLE_CLIENT_ID) {
  // eslint-disable-next-line no-console
  console.error(
    "[auth] REACT_APP_GOOGLE_CLIENT_ID is not set in this build. " +
      "Google sign-in is disabled. Add it to .env.production and rebuild."
  );
}

const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </GoogleOAuthProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// Hydrate the prerendered markup instead of replacing it.
//
// scripts/prerender.js writes a fully rendered page into build/<route>/index.html
// for ~245 routes. createRoot().render() ignores all of it: React discards the
// server markup and rebuilds from nothing, so a visitor watched the landing page
// paint the prerendered hero, get wiped, show a logo, rebuild the hero, and only
// then settle. Four paints for one page, and the first three were work already
// done at build time being thrown away.
//
// hydrateRoot attaches to the existing DOM instead. Where the client render
// disagrees with the prerendered markup React re-renders that subtree on its
// own, which is exactly what happened to the whole page before, so a mismatch
// degrades to today's behaviour rather than to something worse.
//
// The empty-container branch matters: dev server and any route the prerenderer
// skipped ship the bare CRA shell, and hydrating nothing throws.
// onRecoverableError is where React hands over the errors it recovered from and
// otherwise only mentions as "Minified React error #418" in the console. Those
// are not cosmetic here: #418 plus #423 mean React threw the prerendered HTML
// away and client-rendered the whole root, so all 263 prerendered pages stop
// paying for themselves for every visitor.
//
// Kept in production, not just under the debug flag, because an error nobody
// can see is one nobody fixes. The componentStack is the part that names the
// culprit, and it is populated in a HYDRATION_DEBUG build.
const onRecoverableError = (error, errorInfo) => {
  logger.error(
    '[hydration] recovered from:',
    (error && error.message) || error,
    (errorInfo && errorInfo.componentStack) || ''
  );
};

if (container.hasChildNodes()) {
  // Have this route's chunk in hand BEFORE hydrating.
  //
  // Hydration that reaches a Suspense boundary whose chunk has not arrived
  // tears the prerendered content out and paints it again, undoing the whole
  // point of prerendering. Routes that are text pages are imported eagerly
  // instead; this covers the /social family, which is an application surface
  // and would cost every visitor who never opens it. See MainRoute.js.
  //
  // Failure is not fatal: hydrating without the chunk is exactly today's
  // behaviour, so the catch leaves things no worse than before.
  // Deferred ONLY when there is a chunk to wait for.
  //
  // The first version always went through the promise, so hydration started a
  // microtask later on every route including the ones with nothing to preload.
  // That is not free: production went from 9 clean routes to 7, with #421
  // ("this Suspense boundary received an update before it finished hydrating")
  // appearing on /incidents, /download and /contact, which had been fine. Those
  // are races, and delaying the start of hydration moves them.
  //
  // So every route hydrates synchronously exactly as before, and only the ones
  // that name a chunk wait for it.
  const pending = selectChunkLoaders(window.location.pathname);
  if (pending.length) {
    preloadPrerenderedChunk(window.location.pathname)
      .catch(() => {})
      .then(() => {
        ReactDOM.hydrateRoot(container, tree, { onRecoverableError });
      });
  } else {
    ReactDOM.hydrateRoot(container, tree, { onRecoverableError });
  }
} else {
  ReactDOM.createRoot(container).render(tree);
}

// One delegated listener for every link on the site, installed once outside
// React because it is a document-level concern rather than a component's.
// Links added later are measured without anyone remembering to wire them up.
// See src/utils/linkAttribution.js for what is recorded and what is not.
installLinkAttribution();
